import {isFeatureEnabled} from 'feature-flags';
import {IconButton, Label} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNotNil} from 'ramda';

import {type AddrDetailApiResponse} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {AddressSearch, useAddress} from '@dms/shared';

import {useOnMount} from 'shared/web';

import {useFilters} from '../../../../FiltersContext/FiltersContext';

const DEFAULT_LOCATION_RADIUS_KM = 50;
const LOCATION_TIMEOUT_MS = 10000;

export function FilterLocation() {
  const {filters, onUpdateFilters} = useFilters();
  const {composeAddress} = useAddress();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isActive = useRef(true);
  useOnMount(
    () => {
      isActive.current = true;
    },
    () => {
      isActive.current = false;
    }
  );

  const address = filters.address;
  const searchText =
    address && (isFeatureEnabled(featureFlags.SOURCING_FILTER_REDESIGN) || isNotNil(filters.radius))
      ? composeAddress({
          street: address.street ?? '',
          descriptiveNumber: address.descriptiveNumber ?? null,
          orientationNumber: address.orientationNumber ?? null,
          city: address.city ?? '',
          zip: address.zipCode ?? '',
          addressComplement: null,
          country: address.country ?? '',
          prefix: address.prefix ?? null,
          district: null,
          state: address.state ?? null,
        })
      : null;

  const handleSelectAddress = (selectedAddress: AddrDetailApiResponse) => {
    onUpdateFilters('address', selectedAddress);
    if (
      isFeatureEnabled(featureFlags.SOURCING_FILTER_REDESIGN) &&
      isNotNil(selectedAddress?.gps?.lat) &&
      isNotNil(selectedAddress?.gps?.lng)
    ) {
      setHasError(false);
      onUpdateFilters('radius', (radius) =>
        radius && radius > 0 ? radius : DEFAULT_LOCATION_RADIUS_KM
      );
    }
  };

  const handleUseCurrentLocation = () => {
    setHasError(false);
    if (!navigator.geolocation) {
      setHasError(true);
      return;
    }
    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      ({coords}) => {
        if (!isActive.current) {
          return;
        }
        setIsLoading(false);
        handleSelectAddress({
          city: i18n.t('page.filters.labels.currentLocation'),
          gps: {lat: String(coords.latitude), lng: String(coords.longitude)},
        });
      },
      () => {
        if (!isActive.current) {
          return;
        }
        setIsLoading(false);
        setHasError(true);
      },
      {timeout: LOCATION_TIMEOUT_MS, maximumAge: 0}
    );
  };

  return (
    <VStack>
      <Label>{i18n.t('page.filters.labels.location')}</Label>
      <Box backgroundColor="palettes.neutral.20.100" borderRadius="small">
        <HStack align="center">
          <Box flex={1} minWidth={0}>
            <AddressSearch initialSearchText={searchText} label="" onSelect={handleSelectAddress} />
          </Box>
          <Show whenFeatureEnabled={featureFlags.SOURCING_FILTER_REDESIGN}>
            <Box paddingRight={2}>
              <IconButton
                aria-label={i18n.t('page.filters.actions.useCurrentLocation')}
                icon="maps/place"
                size="small"
                isLoading={isLoading}
                isDisabled={isLoading || !isFeatureEnabled(featureFlags.SOURCING_FILTER_REDESIGN)}
                onClick={handleUseCurrentLocation}
              />
            </Box>
          </Show>
        </HStack>
      </Box>
      <Show whenFeatureEnabled={featureFlags.SOURCING_FILTER_REDESIGN}>
        <Show when={hasError}>
          <Box paddingTop={1}>
            <Text size="small" color="danger">
              {i18n.t('page.filters.notifications.locationUnavailable')}
            </Text>
          </Box>
        </Show>
      </Show>
    </VStack>
  );
}
